/**
 * O que se vê no chão da sala: os corpos, o feixe do campo, a SAÍDA e o PERIGO.
 * three.js PURO (ADR-0003), mesma regra do `World`: NEVER importe react, NEVER
 * invente número, NEVER simule nada.
 *
 * O feixe NEVER é enfeite. O `first_prototype` do PULSO pede `feedback essencial`
 * junto com o cubo cinza, e a pergunta que ele quer responder é "o jogador entende
 * por que às vezes ele se move e às vezes o objeto se move?". Sem ver O QUE o campo
 * pegou, essa pergunta NEVER tem como ser respondida com honestidade.
 *
 * Alocação acontece quando o corpo entra na cena, NEVER por frame.
 */
import { tokens } from '@game/design';
import { balance, roomFor } from '@game/gamedata';
import type { BodyKind } from '@game/sim';
import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Color,
  DynamicDrawUsage,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshLambertMaterial,
  RingGeometry,
  type Scene,
} from 'three';

/** O que o render precisa de um corpo. Encaixa direto no `BodyState` do sim. */
export interface BodyView {
  readonly id: number;
  /**
   * O tipo vem do `sim`, e NEVER de uma união copiada aqui. A cópia existia e
   * custou: acrescentar o Núcleo quebrou o build em dois arquivos que NEVER
   * tinham motivo para saber a lista inteira de tipos de corpo. `Record<BodyKind,
   * ...>` na geometria é o que transforma "tipo novo" em erro de compilação, em
   * vez de um corpo invisível na sala.
   */
  readonly kind: BodyKind;
  readonly x: number;
  readonly z: number;
  readonly heldBy: number;
}

/** De onde sai o feixe e para onde vai. Ausente = campo desligado. */
export interface FieldView {
  readonly fromX: number;
  readonly fromZ: number;
  readonly targetId: number;
}

export class Bodies {
  private readonly meshes = new Map<number, Mesh>();
  private readonly geometries: Record<BodyView['kind'], BoxGeometry>;
  private readonly idleMaterial: MeshLambertMaterial;
  private readonly coreMaterial: MeshLambertMaterial;
  private readonly activeMaterial: MeshLambertMaterial;
  private readonly beam: Line;
  private readonly exitMark: Mesh;
  private readonly exitGeometry: RingGeometry;
  private readonly exitMaterial: MeshLambertMaterial;
  private readonly hazardMaterial: MeshLambertMaterial;
  private readonly hazardMarks: Mesh[] = [];
  private readonly beamPositions: BufferAttribute;
  private readonly beamGeometry: BufferGeometry;
  private readonly beamMaterial: LineBasicMaterial;

  /**
   * A saída ENTRA, e NEVER é lida de uma flag aqui: `render/` é three puro e
   * NEVER decide qual conteúdo está no ar (invariante 3). Quem sabe a flag é
   * `main.ts`, e o padrão é a sala de protótipo — que é a que vai ao ar.
   */
  constructor(
    private readonly scene: Scene,
    exitZone: { readonly x: number; readonly z: number; readonly radius: number } = roomFor(false)
      .objective.exit,
  ) {
    this.geometries = {
      anchor: cube(balance.bodies.anchor.radius),
      light: cube(balance.bodies.light.radius),
      heavy: cube(balance.bodies.heavy.radius),
      core: cube(balance.bodies.core.radius),
    };
    // Cinza: a sala do protótipo NEVER tem arte, e cor de verdade é fase 03.
    this.idleMaterial = new MeshLambertMaterial({
      color: new Color(tokens.color.role.textSecondary),
    });
    // O Núcleo NEVER pode ser mais um caixote cinza. Ele é o OBJETIVO, e a sala
    // do protótipo é cinza justamente para que a única coisa colorida signifique
    // alguma coisa: o Núcleo e a saída usam a MESMA cor, e é esse par que diz ao
    // jogador, sem tutorial, o que vai para onde.
    this.coreMaterial = new MeshLambertMaterial({ color: new Color(tokens.color.semantic.ok) });
    // O alvo magnetizado acende. É o feedback mínimo do auto-lock.
    this.activeMaterial = new MeshLambertMaterial({
      color: new Color(tokens.color.role.accentText),
    });

    this.beamGeometry = new BufferGeometry();
    this.beamPositions = new BufferAttribute(new Float32Array(6), 3);
    // O feixe muda de ponta todo frame enquanto o botão está apertado.
    this.beamPositions.setUsage(DynamicDrawUsage);
    this.beamGeometry.setAttribute('position', this.beamPositions);
    this.beamMaterial = new LineBasicMaterial({
      color: new Color(tokens.color.role.accentText),
    });
    // A SAÍDA: anel no chão, do tamanho exato da zona que a sala mede. Anel, e
    // NEVER disco: disco esconde o piso e vira "buraco", e o jogador precisa ver
    // que dá para ANDAR ali dentro.
    //
    // `semantic.ok` é a mesma cor da frase de vitória (`ui/match-end.ts`): a única
    // coisa verde da partida é o que termina bem.
    const exit = exitZone;
    this.exitGeometry = new RingGeometry(exit.radius - 0.35, exit.radius, 48);
    this.exitMaterial = new MeshLambertMaterial({ color: new Color(tokens.color.semantic.ok) });
    this.exitMark = new Mesh(this.exitGeometry, this.exitMaterial);
    this.exitMark.rotation.x = -Math.PI / 2;
    // Mesma folga da borda da arena: coplanar com o piso dá z-fighting em GPU móvel.
    this.exitMark.position.set(exit.x, 0.02, exit.z);
    this.exitMark.visible = false;
    this.scene.add(this.exitMark);

    // O perigo é VERMELHO e a saída é verde, e o par é a única cor da sala junto
    // com o Núcleo: numa sala cinza, cor significa consequência.
    this.hazardMaterial = new MeshLambertMaterial({
      color: new Color(tokens.color.semantic.danger),
    });

    this.beam = new Line(this.beamGeometry, this.beamMaterial);
    this.beam.visible = false;
    // Fora do frustum culling: a caixa do feixe muda a cada frame, e recalcular
    // não vale por uma linha só — sem isto ele some em ângulo de câmera.
    this.beam.frustumCulled = false;
    this.scene.add(this.beam);
  }

  /**
   * As zonas de perigo, ditas pelo SERVIDOR no `welcome`. Chamado UMA vez por
   * partida: a zona NEVER muda no meio, e recriar geometria por frame seria lixo
   * a 60 fps para desenhar um círculo parado.
   *
   * Substitui o que houver: entrar em outra sala NEVER empilha marca de chão.
   */
  setHazards(zones: readonly { x: number; z: number; radius: number }[]): void {
    for (const mark of this.hazardMarks) {
      this.scene.remove(mark);
      mark.geometry.dispose();
    }
    this.hazardMarks.length = 0;

    for (const zone of zones) {
      // Faixa mais GROSSA que a da saída: a saída é um alvo, e o perigo é uma
      // área — anel fino leria como "círculo seguro", que é o oposto do que é.
      const geometry = new RingGeometry(Math.max(zone.radius - 0.6, 0.01), zone.radius, 48);
      const mark = new Mesh(geometry, this.hazardMaterial);
      mark.rotation.x = -Math.PI / 2;
      // Mesma folga do resto do chão: coplanar dá z-fighting em GPU móvel.
      mark.position.set(zone.x, 0.02, zone.z);
      this.scene.add(mark);
      this.hazardMarks.push(mark);
    }
  }

  meshFor(id: number): Mesh | undefined {
    return this.meshes.get(id);
  }

  get beamVisible(): boolean {
    return this.beam.visible;
  }

  /**
   * Traz a cena para o estado que o sim descreveu. Mesma varredura O(n²) do
   * `World`, e pelo mesmo motivo: a sala cinza cabe numa mão.
   */
  sync(bodies: readonly BodyView[], field: FieldView | undefined): void {
    for (const body of bodies) {
      let mesh = this.meshes.get(body.id);
      if (mesh === undefined) {
        mesh = new Mesh(this.geometries[body.kind], this.idleMaterial);
        this.meshes.set(body.id, mesh);
        this.scene.add(mesh);
      }
      const magnetized = field?.targetId === body.id || body.heldBy !== 0;
      const parado = body.kind === 'core' ? this.coreMaterial : this.idleMaterial;
      // Magnetizado ganha da cor do Núcleo: quem está com o campo preso precisa
      // ver O QUE ele pegou, e essa leitura NEVER pode depender do tipo do corpo.
      mesh.material = magnetized ? this.activeMaterial : parado;
      mesh.position.set(body.x, balance.bodies[body.kind].radius, body.z);
    }

    for (const [id, mesh] of this.meshes) {
      let present = false;
      for (const body of bodies) {
        if (body.id === id) {
          present = true;
          break;
        }
      }
      if (present) continue;
      this.scene.remove(mesh);
      this.meshes.delete(id);
    }

    this.syncBeam(field);
    // A saída aparece porque existe NÚCLEO no mundo, e NEVER porque uma flag do
    // cliente disse que sim. Quem liga a fatia é o servidor (flag `objective`), e
    // um interruptor paralelo aqui daria a sala com saída desenhada e sem Núcleo
    // — uma promessa que o mundo NEVER pode cumprir.
    this.exitMark.visible = bodies.some((body) => body.kind === 'core');
  }

  dispose(): void {
    for (const mesh of this.meshes.values()) this.scene.remove(mesh);
    this.meshes.clear();
    for (const geometry of Object.values(this.geometries)) geometry.dispose();
    this.idleMaterial.dispose();
    this.coreMaterial.dispose();
    this.activeMaterial.dispose();
    for (const mark of this.hazardMarks) {
      this.scene.remove(mark);
      mark.geometry.dispose();
    }
    this.hazardMarks.length = 0;
    this.hazardMaterial.dispose();
    this.scene.remove(this.exitMark);
    this.exitGeometry.dispose();
    this.exitMaterial.dispose();
    this.scene.remove(this.beam);
    this.beamGeometry.dispose();
    this.beamMaterial.dispose();
  }

  private syncBeam(field: FieldView | undefined): void {
    const target = field === undefined ? undefined : this.meshes.get(field.targetId);
    if (field === undefined || target === undefined) {
      this.beam.visible = false;
      return;
    }
    // Na altura do peito do robô: no chão a linha some dentro do piso.
    const height = balance.player.radius;
    this.beamPositions.setXYZ(0, field.fromX, height, field.fromZ);
    this.beamPositions.setXYZ(1, target.position.x, height, target.position.z);
    this.beamPositions.needsUpdate = true;
    this.beam.visible = true;
  }
}

function cube(radius: number): BoxGeometry {
  const side = radius * 2;
  return new BoxGeometry(side, side, side);
}
